<template>
  <div></div>
</template>

<script>
// ─────────────────────────────────────────────────────────────────────────────
// MqttManager — real-time command-center channel for Camera Bypass & Temp Disarm.
// Mirrors chekt-web-monitoring-v2 (MonitoringMqtt.vue): connects to the command
// center over wss, subscribes to chekt/sites/{siteId}, and reconnects on a timer.
//
// Per the API contract, MQTT is only a NUDGE — it carries no authoritative state
// and doesn't replay missed events. On every relevant message we re-fetch the
// list endpoint (the source of truth); the pending approval record then lands in
// the store and BypassApprovalDialog auto-shows.
//
// The channel is gated by the dealer beta flag (dp_beta_redirect) via `enabled`:
// dealers without the feature never connect or subscribe (connect() also still
// guards on the MQTT token + selectedSiteId).
// ─────────────────────────────────────────────────────────────────────────────

// Readable dev trace. Flip MQTT_LOG to false to silence. Renders as a coloured
// "MQTT" chip in the console so it's easy to spot/filter; pass a 2nd arg to dump
// the payload (expandable in devtools).
const MQTT_LOG = true
const MQTT_CHIP_CSS = 'background:#8e44ad;color:#fff;padding:1px 6px;border-radius:3px;font-weight:bold'
function mlog (headline, detail) {
  if (!MQTT_LOG) return
  if (detail !== undefined) console.log('%cMQTT%c ' + headline, MQTT_CHIP_CSS, 'color:#8e44ad', detail)
  else console.log('%cMQTT%c ' + headline, MQTT_CHIP_CSS, 'color:#8e44ad')
}
// Collapsed group for an incoming message: one clickable header, and inside it a
// field-by-field breakdown + the full pretty-printed JSON, so exactly what came
// in is easy to inspect (and copy) without hunting through a raw object.
function mlogMessage (topic, body) {
  if (!MQTT_LOG) return
  const p = (body && body.payload) || {}
  const head = (p.event_type || body.subject || body.type || '?')
  const ar = p.approval_request || null
  console.groupCollapsed('%cMQTT%c 📨 ' + topic + '  ·  ' + head, MQTT_CHIP_CSS, 'color:#8e44ad')
  console.log('subject          :', body.subject)
  console.log('type             :', body.type)
  console.log('event_type       :', p.event_type)
  console.log('site_id          :', p.site_id)
  if (ar) {
    console.log('approval action  :', ar.action)
    console.log('approval status  :', ar.status)
    console.log('approval_request :', ar)
  }
  if (p.camera_bypass) console.log('camera_bypass    :', p.camera_bypass)
  if (p.temp_disarm) console.log('temp_disarm      :', p.temp_disarm)
  console.log('payload          :', p)
  console.log('raw JSON         :\n' + JSON.stringify(body, null, 2))
  console.groupEnd()
}

export default {
  name: 'mqtt-manager',
  computed: {
    // MQTT exists solely for Camera Bypass / Temp Disarm / Approval, so when the
    // dealer beta (dp_beta_redirect) is off there is nothing to listen for —
    // don't open the socket or subscribe at all. Flipping this re-runs the
    // watcher below, which connects or disconnects accordingly.
    enabled: function () {
      return this.$store.getters.isBypassApprovalEnabled
    },
    accessToken: function () {
      return this.$store.getters.myMqttAccessToken
    },
    selectedSiteId: function () {
      return this.$store.getters.selectedSiteId
    },
  },
  data: function () {
    return {
      connectInterval$i: null,
      mqttClient: null,
      isConnected: false,
      subscribedSiteId: null,
    }
  },
  watch: {
    // Re-subscribe to the new site's topic when the selection changes.
    selectedSiteId: function () {
      if (!this.enabled) return
      if (this.isConnected) {
        this.removeSiteSubscription()
        this.addSiteSubscription()
      } else {
        this.startConnectionInterval()
      }
    },
    // Once the profile (and its MQTT token) loads, start connecting.
    accessToken: function (now) {
      if (this.enabled && now) this.startConnectionInterval()
    },
    enabled: function (now) {
      if (now) this.startConnectionInterval()
      else this.disconnect()
    },
  },
  created: function () {
    if (this.enabled) this.startConnectionInterval()
  },
  beforeDestroy: function () {
    this.disconnect()
  },
  methods: {
    startConnectionInterval: function () {
      const retryMs = 1000 * 5 // 5s (reference monitoring-v2 pattern)
      this.stopConnectionInterval()
      if (!this.isConnected) this.connect()
      this.connectInterval$i = setInterval(() => {
        if (!this.isConnected) this.connect()
      }, retryMs)
    },
    stopConnectionInterval: function () {
      if (this.connectInterval$i) {
        clearInterval(this.connectInterval$i)
        this.connectInterval$i = null
      }
    },
    disconnect: function () {
      this.stopConnectionInterval()
      if (this.mqttClient) {
        this.removeSiteSubscription()
        try { this.mqttClient.end(true) } catch (e) { /* noop */ }
        this.mqttClient = null
      }
      this.isConnected = false
    },
    connect: function () {
      if (!this.enabled || !this.accessToken || !this.selectedSiteId) return
      if (!this.$env.mqttTargetPath) return
      if (this.mqttClient) return // a connect attempt is already in flight

      const url = 'wss://' + this.$env.mqttTargetPath + ':' + this.$env.mqttTargetPathPort
      const options = {
        username: 'app',
        password: this.accessToken,
        clientId: 'enduser_app_' + Math.random().toString(16).substr(2, 8),
        keepalive: 50,
      }

      this.mqttClient = this.$mqtt.connect(url, options)

      mlog('connecting → ' + url + '  (site ' + this.selectedSiteId + ')')

      this.mqttClient.on('connect', function () {
        this.isConnected = true
        mlog('✅ connected')
        this.addSiteSubscription()
        // Re-sync on every (re)connect: a random clientId + clean session means
        // the broker does NOT replay messages published while we were offline,
        // so re-fetch the list to catch anything missed (spec E-API-11/12).
        this.resyncFromServer()
      }.bind(this))

      this.mqttClient.on('error', function (error) {
        mlog('⛔ error', error)
        console.error('MqttManager: error', error)
        this.isConnected = false
      }.bind(this))

      this.mqttClient.on('close', function () {
        mlog('🔌 connection closed')
        this.isConnected = false
      }.bind(this))

      this.mqttClient.on('offline', function () {
        mlog('📴 offline')
        this.isConnected = false
      }.bind(this))

      this.mqttClient.on('message', function (topic, message) {
        this.onNewMessage(topic, message)
      }.bind(this))
    },
    resyncFromServer: function () {
      const siteId = this.selectedSiteId
      if (!siteId) return
      if (!this.$store.getters.isBypassApprovalEnabled) return
      mlog('🔄 resync → refetch bypasses + temp-disarms  (site ' + siteId + ')')
      // Fire-and-forget; the actions swallow their own errors, but guard anyway.
      Promise.resolve(this.$store.dispatch('fetchBypasses', { siteId })).catch(function () {})
      Promise.resolve(this.$store.dispatch('fetchTempDisarms', { siteId })).catch(function () {})
    },
    addSiteSubscription: function () {
      if (this.isConnected && this.mqttClient && this.selectedSiteId) {
        this.subscribedSiteId = this.selectedSiteId
        this.mqttClient.subscribe('chekt/sites/' + this.selectedSiteId, { qos: 1 })
        mlog('📡 subscribed → chekt/sites/' + this.selectedSiteId)
      }
    },
    removeSiteSubscription: function () {
      if (this.mqttClient && this.subscribedSiteId) {
        this.mqttClient.unsubscribe('chekt/sites/' + this.subscribedSiteId)
        this.subscribedSiteId = null
      }
    },
    onNewMessage: function (topic, message) {
      try {
        const body = JSON.parse(message.toString())
        mlogMessage(topic, body)
        if (body.type !== 'notification') {
          mlog('   ↩ ignored (type=' + body.type + ', not a notification)')
          return
        }
        // Normally we're not even connected when the beta is off; this covers the
        // brief window where the flag flips while messages are still in flight.
        if (!this.$store.getters.isBypassApprovalEnabled) return
        const subject = body.subject
        const payload = body.payload || {}
        const siteId = payload.site_id || this.selectedSiteId
        const eventType = ('' + (payload.event_type || '')).toLowerCase()
        const action = ('' + ((payload.approval_request && payload.approval_request.action) || '')).toLowerCase()

        // Only Camera Bypass & Temp Disarm messages concern us (spec MqttEnvelope:
        // subject 'camera_bypass', event_type approval_request.* / camera_bypass.* /
        // temp_disarm.changed). Ignore everything else (events/privacy handled elsewhere).
        const relevant = subject === 'camera_bypass' ||
          subject === 'event/temp-disarm/cancelled' ||
          eventType.indexOf('approval_request') === 0 ||
          eventType.indexOf('camera_bypass') === 0 ||
          eventType.indexOf('temp_disarm') === 0
        const label = (eventType || subject || '?') + (action ? ' [' + action + ']' : '')
        if (!relevant) {
          mlog('   ↩ ignored (not bypass/temp): ' + label)
          return
        }

        let doBypass = eventType.indexOf('camera_bypass') === 0 || action === 'bypass' || action === 'clear_bypass'
        let doTemp = eventType.indexOf('temp_disarm') === 0 ||
          subject === 'event/temp-disarm/cancelled' ||
          action === 'temp_disarm' || action === 'clear_temp_disarm' || action === 'arm' || action === 'disarm'
        // Ambiguous (e.g. approval_request.* with no action) → refresh both (cheap, safe).
        if (!doBypass && !doTemp) { doBypass = true; doTemp = true }

        // A resolved partition approval (arm/disarm/temp_disarm cancelled by the
        // monitoring center, or approved/declined/expired elsewhere): remove that
        // exact request now. The pending list is eventually-consistent, so a
        // blanket refetch can't be trusted to drop it (and could even re-add it),
        // so we skip the temp-disarm refetch and let the store reconcile by id.
        const ar = payload.approval_request || null
        const arStatus = ('' + ((ar && ar.status) || '')).toLowerCase()
        const isResolved = eventType.indexOf('approval_request.resolved') === 0 ||
          (eventType.indexOf('approval_request') === 0 && arStatus && arStatus !== 'pending')
        if (isResolved && ar) {
          // Remember the resolved id for EVERY action (bypass included, which
          // reconciles through fetchBypasses) so the eventually-consistent
          // pending list can't resurrect it and re-open the approval modal.
          this.$store.dispatch('noteApprovalResolved', { approvalRequest: ar })
        }
        if (isResolved && ar && (action === 'arm' || action === 'disarm' || action === 'temp_disarm')) {
          this.$store.dispatch('resolveApprovalRequest', { approvalRequest: ar, siteId })
          doTemp = false
        }

        const refetch = [doBypass ? 'bypass' : null, doTemp ? 'temp-disarm' : null].filter(Boolean).join(' + ')
        mlog('   ✔ ' + label + '  → refetch: ' + refetch + '  (site ' + siteId + ')')

        if (doBypass) this.$store.dispatch('fetchBypasses', { siteId })
        if (doTemp) this.$store.dispatch('fetchTempDisarms', { siteId })
      } catch (err) {
        mlog('⛔ onNewMessage parse/handle failed', err)
        console.error('MqttManager: onNewMessage', err)
      }
    },
  },
}
</script>

<style scoped>
</style>
